import { Box, LinearProgress, Typography } from "@mui/material";
import { useAtomValue } from "jotai";
import { AppAtoms } from "../../lib/store";

interface UsageBarProps {
  label: string;
  current: number;
  max: number;
  unit?: string;
  dark?: boolean;
}

function UsageBar({ label, current, max, unit = "", dark }: UsageBarProps) {
  const ratio = max > 0 ? Math.min(current / max, 1) : 0;
  const pct = Math.round(ratio * 100);

  // カタログ #18 meter系トークン転写
  // fill: 通常=kohaku/kohaku-light(dark)、warn>=80%=sem-warning、full=sem-error
  const fillColor =
    ratio >= 1.0
      ? "var(--sem-error)"
      : ratio >= 0.8
      ? "var(--sem-warning)"
      : dark
      ? "var(--color-kohaku-light)"
      : "var(--color-kohaku)";

  const trackColor = dark ? "var(--ov-light-15)" : "var(--color-border-light)";
  const labelColor = dark ? "var(--color-ai-pale)" : "var(--color-sumi-light)";
  const labelFontSize = dark ? "var(--fs-11)" : "var(--fs-12)";
  // track: dark=5px/radius-3px、light=8px/radius-4px（カタログ実測）
  const trackHeight = dark ? 5 : 8;
  const trackRadius = dark ? "3px" : "4px";

  return (
    <Box sx={{ mb: dark ? "var(--sp-2)" : "var(--sp-3)" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: "var(--px-3)" }}>
        <Typography sx={{ fontSize: labelFontSize, color: labelColor }}>{label}</Typography>
        <Typography sx={{ fontSize: labelFontSize, color: labelColor }}>
          {current} / {max}{unit}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: trackHeight,
          borderRadius: trackRadius,
          bgcolor: trackColor,
          "& .MuiLinearProgress-bar": {
            bgcolor: fillColor,
            borderRadius: trackRadius,
            transition: "none",
          },
        }}
      />
    </Box>
  );
}

interface PlanUsageCardProps {
  dark?: boolean;
}

export function PlanUsageCard({ dark }: PlanUsageCardProps) {
  const tenantPlan = useAtomValue(AppAtoms.tenantPlanAtom);
  if (!tenantPlan) return null;

  const { plan_name, max_partners, max_et_sites, current_partners, current_et_sites } = tenantPlan;
  const showPartners = max_partners > 0;
  const showEtSites = max_et_sites > 0;
  if (!showPartners && !showEtSites) return null;

  // カタログ #18:
  // dark=po-plan-card（サイドバー暗面文脈）: ov-light-06 bg / ov-light-12 border
  // light=po-card.panel（明背景文脈）: color-white bg / color-border-light border
  const containerSx = dark
    ? {
        m: "var(--sp-3)",
        p: "var(--sp-3)",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--ov-light-12)",
        bgcolor: "var(--ov-light-06)",
        fontSize: "var(--fs-11)",
        color: "var(--color-ai-pale)",
      }
    : {
        p: "var(--sp-4) var(--sp-5)",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--color-border)",
        bgcolor: "var(--color-white)",
      };

  const nameColor = dark ? "var(--color-kohaku-light)" : "var(--color-ai)";
  const nameFontSize = dark ? "var(--fs-12)" : "var(--fs-13)";
  const nameMb = dark ? "var(--sp-2)" : "var(--sp-3)";

  return (
    <Box
      data-testid={dark ? "plan-usage-card-dark" : "plan-usage-card-light"}
      sx={containerSx}
    >
      <Typography
        sx={{
          fontSize: nameFontSize,
          fontWeight: "var(--fw-bold)",
          color: nameColor,
          mb: nameMb,
        }}
      >
        {plan_name ?? "利用状況"}
      </Typography>
      {showPartners && (
        <UsageBar
          label="パートナー"
          current={current_partners}
          max={max_partners}
          unit="社"
          dark={dark}
        />
      )}
      {showEtSites && (
        <UsageBar
          label="外部ETサイト"
          current={current_et_sites}
          max={max_et_sites}
          unit=""
          dark={dark}
        />
      )}
    </Box>
  );
}
